import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ShieldCheck, ArrowLeft } from 'lucide-react'
import DocumentUpload from '@components/web/DocumentUpload'
import Button from '@components/web/Button'
import { clsx } from 'clsx'
import { patientAuthService } from '@lib/supabase/patientAuth.service'

type DocType = 'passport' | 'voters-card' | 'nin-slip'

const DOC_OPTIONS: { value: DocType; label: string; desc: string }[] = [
  { value: 'passport', label: 'International Passport', desc: 'Valid travel passport' },
  { value: 'voters-card', label: "Voter's Card", desc: 'INEC Voter registration card' },
  { value: 'nin-slip', label: 'NIN Slip', desc: 'National Identity Number slip' },
]

export default function IdentityVerification() {
  const navigate = useNavigate()
  const [docType, setDocType] = useState<DocType | ''>('')
  const [file, setFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ docType?: string; file?: string; general?: string }>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: typeof errors = {}
    if (!docType) newErrors.docType = 'Please select a document type'
    if (!file) newErrors.file = 'Please upload your document'
    if (Object.keys(newErrors).length) { setErrors(newErrors); return }

    setErrors({})
    setIsLoading(true)
    const result = await patientAuthService.submitIdentityDocument(docType as DocType, file!)
    setIsLoading(false)

    if (!result.success) {
      setErrors({ general: result.error || 'Upload failed. Please try again.' })
      return
    }

    navigate('/auth/verification-pending')
  }

  return (
    <div className="bg-card rounded-2xl shadow-lg p-8 w-full">
      <Link to="/auth/verify-email" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="mb-6">
        <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <ShieldCheck size={26} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Verify Your Identity</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Upload a government-issued ID to verify your identity.
        </p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{errors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Document Type <span className="text-destructive">*</span>
          </label>
          <div className="space-y-2">
            {DOC_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDocType(opt.value)}
                className={clsx(
                  'w-full text-left p-3 rounded-lg border transition-all',
                  docType === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={clsx('w-4 h-4 rounded-full border-2 shrink-0', docType === opt.value ? 'border-primary bg-primary' : 'border-border')} />
                  <div>
                    <p className="font-medium text-sm text-foreground">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {errors.docType && <p className="text-xs text-destructive">{errors.docType}</p>}
        </div>

        <DocumentUpload label="Upload Document" value={file} onChange={setFile} error={errors.file} hint="JPG, PNG or PDF — clear photo of your document" />

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          Submit for Verification
        </Button>
      </form>

      <p className="text-xs text-muted-foreground text-center mt-4">
        Your documents are encrypted and stored securely.
      </p>
    </div>
  )
}
